import { atom, type Atom, type PrimitiveAtom } from 'jotai';

import { QueryBuilderIsDebugEnabledAtom } from '../../state/QueryBuilderAtoms';

// Session only: simulations must not persist into a later customer session.
export const ERROR_SCENARIOS = [
	{ value: 'none', label: 'No simulated error', surface: 'none' },
	{ value: 'general', label: '5. General execution failure', surface: 'execution' },
	{ value: 'context', label: '6, 7, 8. Site context failure', surface: 'execution' },
	{ value: 'permission', label: '19. Query access denied (suppressed)', surface: 'execution' },
	{ value: 'network', label: '20. Network restriction (suppressed)', surface: 'execution' },
	{ value: 'account', label: '21. Missing account (suppressed)', surface: 'execution' },
	{ value: 'null', label: '22. Null response (suppressed)', surface: 'execution' },
	{ value: 'upload', label: '22. Upload token failure (suppressed)', surface: 'execution' },
	{ value: 'partial', label: '23. Partial results (errors suppressed)', surface: 'execution' },
	{ value: 'sites', label: '26. Site loading failure', surface: 'site' },
	{ value: 'noSites', label: '27, 28. No available sites', surface: 'site' },
	{
		value: 'semantic',
		label: '46, 47. IDE error with secondary null response',
		surface: 'execution',
	},
	{ value: 'schema', label: '1. Required schema failure', surface: 'schema' },
	{ value: 'optionalSchema', label: '2. Optional schema failure (suppressed)', surface: 'schema' },
	{ value: 'savedLoad', label: '9. Saved query loading failure', surface: 'saved' },
	{ value: 'savedAppMismatch', label: '10. Saved query app mismatch', surface: 'saved' },
	{ value: 'savedSave', label: '11, 12. Save failure', surface: 'save' },
	{ value: 'forgeApps', label: '29. Forge app loading failure', surface: 'apps' },
	{ value: 'noApps', label: '30. No eligible Forge apps', surface: 'apps' },
	{ value: 'preparation', label: '3, 4. Automatic query preparation failed', surface: 'execution' },
	{ value: 'timeout', label: '15. Query timeout', surface: 'execution' },
	{ value: 'rateLimit', label: '17. Request limit', surface: 'execution' },
	{ value: 'unreadable', label: '18. Unexpected response', surface: 'execution' },
	{ value: 'signIn', label: '24, 25. Sign in required', surface: 'execution' },
	{ value: 'noQueries', label: '31. No saved queries yet', surface: 'saved' },
	{ value: 'queryName', label: '41. Missing query name', surface: 'save' },
	{ value: 'forgeApp', label: '42. Missing Forge app selection', surface: 'save' },
	{ value: 'invalidApp', label: '43. Forge app selection no longer valid', surface: 'save' },
] as const;

export type ErrorScenario = (typeof ERROR_SCENARIOS)[number]['value'];
export const SelectedErrorScenarioAtom: PrimitiveAtom<ErrorScenario> = atom<ErrorScenario>('none');
export const ActiveErrorScenarioAtom: Atom<ErrorScenario> = atom((get) =>
	get(QueryBuilderIsDebugEnabledAtom) ? get(SelectedErrorScenarioAtom) : 'none',
);
export const SIMULATED_REQUEST_ID = 'simulated-query-builder-request';
const SIMULATED_TRACE_ID = 'simulated-query-builder-trace';

export function simulatedQueryResponse(scenario: ErrorScenario, successfulData: unknown): unknown {
	const error = (message: string, classification = 'DataFetchingException', statusCode = 500) => ({
		message,
		locations: [],
		extensions: { classification, statusCode },
	});
	const nullError = error(
		"The field at path '/graphStore/cypherQueryV2' was declared as a non null type",
		'NullValueInNonNullableField',
	);
	const messages = {
		general: 'Something went wrong',
		context: 'Query context must not be null and should be a valid platform site or workspace ARI',
		permission: 'You do not have permission to perform the requested action',
		network: 'your IP address is not listed in the IP allowlist',
		account: 'Input accountID cannot be empty',
		upload: 'An error was encountered while attempting to fetch an uploadToken',
	};
	const extensions = {
		gateway: { request_id: SIMULATED_REQUEST_ID, tId: SIMULATED_TRACE_ID },
	};
	const extensionsWithoutTraceId = { gateway: { request_id: SIMULATED_REQUEST_ID } };
	if (scenario === 'semantic')
		return {
			data: { graphStore: null },
			errors: [
				error(
					"Semantic Error: 2:2: Relationship or Logical type 'user-viewed-atlas-goa' does not exist in AGS.",
					'ValidationError',
					400,
				),
				nullError,
			],
			extensions,
		};
	if (scenario === 'partial')
		return { data: successfulData, errors: [error('Something went wrong')], extensions };
	if (scenario === 'null') return { data: { graphStore: null }, errors: [nullError], extensions };
	if (scenario in messages)
		return {
			data: null,
			errors: [error(messages[scenario as keyof typeof messages])],
			extensions: scenario === 'general' ? extensionsWithoutTraceId : extensions,
		};
	if (scenario === 'signIn')
		return {
			data: null,
			errors: [error('UNAUTHENTICATED', 'IncorrectAuthException', 403)],
			extensions,
		};
	return {
		error: 'Simulated request failure',
		name: scenario === 'timeout' ? 'TimeoutError' : 'Error',
		statusCode: scenario === 'rateLimit' ? 429 : 500,
		requestId: SIMULATED_REQUEST_ID,
	};
}
